/**
 * Shared chat-completion fetch helper. Backs both the admin Preview's
 * `AdminChatPanel` and the public `/view/:id` chat panel — single source
 * of truth for the request shape + 409-fallback-to-mock recovery, so
 * the two consumers can never drift apart on the response shape.
 */
import { getEmbedApiContext } from './context';

/**
 * Bridge-only metadata — buttons / options / cards that arrive when the
 * character uses the `chatbot-bridge` provider. The customer's bridge
 * backend returns these alongside `messages`; we render them as
 * interactive UI below the bot bubble and round-trip clicks back via
 * `postback`. LLM-provider replies (openai / custom) leave these
 * undefined.
 */
export interface BridgeButton {
  label: string;
  value: string;
  targetNodeKey?: string;
  href?: string;
}
export interface BridgeOption {
  label: string;
  value: string;
}
export interface BridgeCard {
  title: string;
  description?: string;
  imageUrl?: string;
  buttons?: BridgeButton[];
}

export interface ChatReply {
  text: string;
  /** Action plan envelope returned by model3d characters. Absent for legacy types. */
  actions?: Array<{ type: string; id: string; value?: number }>;
  /** Backwards-compat single-expression field for pngtuber / live2d / rig. */
  expression?: string;
  /** voxcpm2-style-autoselect — per-reply style pick (a member of the
   *  character's cached style list);forwarded to VoxCPM2 as `style`.
   *  Absent when the model omits it. */
  style?: string;
  /** True when the server returned 409 (no API key) and we filled in a mock reply. */
  mock?: boolean;
  /** Bridge-only: row of chips below the reply bubble. */
  buttons?: BridgeButton[];
  /** Bridge-only: same UI as buttons; no href. */
  options?: BridgeOption[];
  /** Bridge-only: stacked column of product / info cards. */
  cards?: BridgeCard[];
  /** Per chat-refund flow — server returns the wallet txnKey for this
   *  chat-spend. Client can POST /api/fan/wallet/chat-refund with this
   *  key if a paid-for post-step (voice-mode TTS) fails. Null when no
   *  spend happened (anonymous visitor, free-tier). */
  chatSpendTxnKey?: string | null;
}

export interface ChatHistoryMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

const MOCK_REPLIES = [
  '嗨嗨！很高興認識你 ✨',
  '我正在思考一個有趣的問題呢...',
  '哇,這個想法不錯!',
  '讓我想想... 嗯嗯,我覺得可以試試看!',
  '有你陪我聊天真好 :)',
  '今天天氣不錯對吧',
  '你希望我幫你做什麼呢?',
];

function mockReply(): string {
  return MOCK_REPLIES[Math.floor(Math.random() * MOCK_REPLIES.length)];
}

/**
 * Optional extras carried alongside the user message. `sessionId`
 * threads through to the chat handler so the chatbot-bridge provider
 * can maintain conversation state on the upstream side. `postback`,
 * when set, makes the chat handler treat this turn as a button click
 * (value-only) instead of free-text — bridge-only semantic.
 */
export interface ChatSendOptions {
  sessionId?: string;
  postback?: string;
  signal?: AbortSignal;
}

/**
 * Send one user message to the per-character chat endpoint. The endpoint
 * routes through whatever provider the character record declares (OpenAI
 * by default, Custom for self-hosted vLLM/Ollama, chatbot-bridge for an
 * external chatbot backend). Returns the reply + (model3d only) the
 * action plan envelope + (bridge only) the buttons / options / cards
 * metadata.
 *
 * @param characterId  the character to chat with
 * @param text         the user's latest message (ignored when opts.postback is set)
 * @param history      optional prior turns; defaults to single-turn
 * @param opts         sessionId + postback + abort signal
 */
export async function sendChat(
  characterId: string,
  text: string,
  history: ChatHistoryMessage[] = [],
  opts: ChatSendOptions = {},
): Promise<ChatReply> {
  // When the caller passes a postback (button click), we leave the
  // user message as empty in the messages array — the chat handler
  // reads `body.postback` directly and forwards it to the bridge in
  // place of natural-language text.
  const messages = opts.postback
    ? history
    : [...history, { role: 'user' as const, content: text }];
  return sendChatRaw(characterId, messages, opts);
}

/**
 * Lower-level variant — caller supplies the full message array verbatim.
 * Used by `embed-idle-small-talk` to inject a one-off system-role
 * steering message ("user has been silent N seconds, nudge them in
 * character") without having to fake it as a user turn. The endpoint
 * accepts any role mix; the persona system prompt is prepended
 * server-side regardless of what's in `messages`.
 */
/**
 * voice-loop iter 20 — fire-and-forget warmup ping. Hits the same
 * chat endpoint with `{ warmup: true }`; the handler skips wallet /
 * memory / affinity / recording and only does a tiny max_tokens=1
 * LLM round-trip. Used on chat-page mount to keep the LLM provider's
 * TCP/TLS connection pool warm so the first real chat call's TTFA
 * doesn't pay handshake cost.
 *
 * Errors are silent — the caller doesn't need to know whether the
 * warmup succeeded; it's purely a side-channel optimization.
 */
export async function warmupChatLlm(characterId: string): Promise<void> {
  try {
    const ctx = getEmbedApiContext();
    await fetch(ctx.apiUrl(`/api/fan/characters/${characterId}/chat`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...ctx.apiAuthHeaders() },
      body: JSON.stringify({ messages: [], warmup: true }),
      keepalive: true,
    });
  } catch { /* fire-and-forget */ }
}

export async function sendChatRaw(
  characterId: string,
  messages: ChatHistoryMessage[],
  opts: ChatSendOptions = {},
): Promise<ChatReply> {
  const ctx = getEmbedApiContext();
  const body: Record<string, unknown> = { messages };
  if (opts.sessionId) body.sessionId = opts.sessionId;
  if (opts.postback) body.postback = opts.postback;
  // Fan-platform clone — hit the requireAuth:true alias so the API
  // Gateway authorizer actually runs and fan JWT context reaches
  // the chat handler. Calling /api/characters/{id}/chat directly
  // goes through the anonymous route (no authorizer) so chat.ts
  // sees authCtx=null and silently skips both the fanId-keyed
  // memory write at chat.ts:760 and the operator-wallet spend at
  // chat.ts:636.
  // This is the only behavioural divergence between the embed-fan
  // clone and embed-client's original chat-client.ts.
  const res = await fetch(ctx.apiUrl(`/api/fan/characters/${characterId}/chat`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...ctx.apiAuthHeaders() },
    body: JSON.stringify(body),
    signal: opts.signal,
  });
  if (!res.ok) {
    if (res.status === 409) {
      return { text: mockReply(), mock: true };
    }
    const data = await res.json().catch(() => null);
    throw new Error(data?.error?.message ?? `HTTP ${res.status}`);
  }
  const reply = (await res.json()) as ChatReply;
  // Per `character-energy-system` — a reply means energy was just spent;
  // tell the battery overlay to refresh immediately (not wait for its
  // heartbeat), so the bar updates per-message instead of in bursts.
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('nx-energy:spent', { detail: { characterId } }));
    // Affinity is credited before the chat response returns. Refresh
    // relationship UI immediately instead of waiting for window focus.
    window.dispatchEvent(new CustomEvent('nx-affinity:changed', { detail: { characterId } }));
  }
  return reply;
}
